/**
 * B2C landing video — timing.
 *
 * Everything is authored on the beat grid of `song.mp3`, measured from the
 * audio (spectral-flux onsets + comb, see the product-film skill's
 * `beats.py`): 129.33 BPM, first beat 29 ms in, onsets within ±3.5 ms of the
 * grid. A beat is 13.92 frames, not 14: rounding it to 14 put the last cuts
 * ~200 ms (6f) behind the music. So cut frames are rounded from the
 * *cumulative* beat time, like the B2B video: every cut stays within half a
 * frame of its beat, with no drift.
 *
 * Change pacing here; `B2CVideo.tsx` only reads these values.
 */

export const B2C_FPS = 30;
/** Measured tempo of `song.mp3` and the time of its first beat. */
export const BPM = 129.33;
const FIRST_BEAT_S = 0.029;
/** Frames per beat (fractional, ≈ 13.92). */
export const BEAT = (B2C_FPS * 60) / BPM;
/** Video frame of a beat index, on the song's grid. */
export const beatFrame = (beats: number) =>
  Math.round(B2C_FPS * FIRST_BEAT_S + beats * BEAT);

/**
 * Scene lengths, in beats. Order = playback order.
 *
 * Rule of thumb (team feedback, v2): animations stay snappy, but once a
 * line has landed it holds for at least ~1.5s + 0.3s per word before the
 * next cut. Lengthen a scene here rather than slowing its animations.
 */
export const SCENE_BEATS = {
  hook: 8, //        "Tu l'as eu où ?" chat → "Tu recommandes déjà."
  trust: 9, //       88 % trust a friend's recommendation (Nielsen)
  unpaid: 12, //     "Et pourtant, tu gagnes…" · 0 € slam · Frak → "+5 €"
  followers: 7, //   "Pas besoin de 100k abonnés"
  pick: 7, //        Step 1 — Explorer, pick a brand
  share: 8, //       Step 2 — share your link (order → story)
  earn: 10, //       Step 3 — friend buys, both win, notification
  cash: 8, //        "Du vrai cash. Pas des points." → bank transfer
  brands: 6, //      Brand wall — "Des marques qui paient leurs ambassadeurs"
  cta: 8, //         End card + stores
} as const;

export type SceneKey = keyof typeof SCENE_BEATS;

export const SCENE_ORDER = Object.keys(SCENE_BEATS) as SceneKey[];

/** Beats elapsed before each scene. */
const BEATS_BEFORE = SCENE_ORDER.reduce(
  (acc, key, i) => {
    const prev = SCENE_ORDER[i - 1];
    acc[key] = prev ? acc[prev] + SCENE_BEATS[prev] : 0;
    return acc;
  },
  {} as Record<SceneKey, number>,
);

const TOTAL_BEATS = SCENE_ORDER.reduce((sum, key) => sum + SCENE_BEATS[key], 0);

/** The first scene starts on frame 0; every later cut sits on its beat. */
export const SCENE_START = SCENE_ORDER.reduce(
  (acc, key, i) => {
    acc[key] = i === 0 ? 0 : beatFrame(BEATS_BEFORE[key]);
    return acc;
  },
  {} as Record<SceneKey, number>,
);

export const B2C_DURATION = beatFrame(TOTAL_BEATS);

/** Scene length in frames (13 or 14 per beat, so the cuts never drift). */
export const sceneFrames = (key: SceneKey) => {
  const i = SCENE_ORDER.indexOf(key);
  const next = SCENE_ORDER[i + 1];
  return (next ? SCENE_START[next] : B2C_DURATION) - SCENE_START[key];
};
